import { useMemo, useState, Fragment, useRef } from 'react';
import { Tooltip, IconButton, TextField, CardActions, TablePagination, Dialog, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Card, Button, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import classNames from 'classnames';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect } from 'react';
import './componentreg.css';
import { customerbasedshift } from '../../Services/app/masterservice';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import Swal from 'sweetalert2'; // Ensure Swal is imported
import { shiftadd } from '../../Services/app/masterservice'; // Ensure shiftadd is imported
import ComponentEdit from '../Componentregistration/componentedit';
import ComponentAdd from '../Componentregistration/componentadd'
import { cleanCustomerId, isPmiCustomer } from '../../Services/app/operatorservice';
import { useUserRole } from '../../Shared/hooks/useUserRole';
import { filterByPlantAccess, filterPlantsForUser } from '../../Shared/utils/machineGroupAccess';
import { resolvePlantName } from '../../Shared/utils/plantRecordCode';
import { Autocomplete } from '@mui/material';
const ComponentRegistration = () => {
    // Separate state for Add and Edit dialogs
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editDialogData, setEditDialogData] = useState(null); // To store data for the edit dialog
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    // dialogOpenCount is used for background color in dialogs, tracks total open dialogs
    const [dialogOpenCount, setDialogOpenCount] = useState(0);
    const [datasource, setDatasource] = useState([]);
    const [expandedComponents, setExpandedComponents] = useState({});
    // Holds the parsed/merged result of an import until the user confirms it.
    // { components: preview rows (only new/changed), allComponents: full set to
    //   save, fetched: server snapshot for backup, stats: change counts }
    const [importPreview, setImportPreview] = useState(null);
    const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [importFileName, setImportFileName] = useState('');
    const [isCommitting, setIsCommitting] = useState(false);
    // Per-row validation errors from the last parsed file. Non-empty means the
    // file is rejected: the dialog shows these rows in red and disables Upload.
    const [importErrors, setImportErrors] = useState([]);
    const customerId = localStorage.getItem('CustomerID');
    const isCustomerId = cleanCustomerId(customerId) === window._env_.BIOTEK_CUSTOMER_ID;
    const fileInputRef = useRef(null);
    const { userRole } = useUserRole();
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    const userPlantCodes = Array.isArray(userDetails?.plantAccess) ? userDetails.plantAccess : [];
    const [plantOptions, setPlantOptions] = useState([]);
    const [selectedPlantFilter, setSelectedPlantFilter] = useState('all');
    // Only worth showing once this user actually has more than one Plant to
    // choose between — a single-plant user has nothing to filter.
    const showPlantFilter = isPmiCustomer(customerId) && plantOptions.length > 1;

    const fetchPlantGroups = async () => {
        try {
            if (!isPmiCustomer(customerId)) {
                setPlantOptions([]);
                return;
            }
            const data = await customerbasedshift(customerId, 'plantgroups');
            const allPlantGroups = data?.[0]?.value || [];
            const userPlantGroups = filterPlantsForUser(allPlantGroups, userRole, userPlantCodes);
            setPlantOptions(userPlantGroups.map((p) => ({ code: p.code, name: p.name || p.code })));
        } catch (error) {
            console.error('Error fetching plant groups:', error);
            setPlantOptions([]);
        }
    };

    useEffect(() => {
        fetchPlantGroups();
    }, [customerId]);

    const getComponentKey = (component, index) => component.id?.$oid || component.id || `idx-${index}`;

    const toggleComponent = (key) => {
        setExpandedComponents((prev) => ({ ...prev, [key]: !prev[key] }));
    };
    const filteredDatasource = useMemo(() => {
        const plantScoped = filterByPlantAccess(datasource, userRole, userPlantCodes);
        const plantFiltered = selectedPlantFilter === 'all'
            ? plantScoped
            : plantScoped.filter((row) => row?.plant_code === selectedPlantFilter);
        const q = searchText.trim().toLowerCase();
        if (!q) return plantFiltered;
        const matches = (val) => String(val ?? '').toLowerCase().includes(q);
        return plantFiltered.filter(row =>
            matches(row.component_name) ||
            matches(row.component_number) ||
            matches(row.operation_number) ||
            matches(row.operation_name) ||
            (Array.isArray(row.operations) && row.operations.some(op =>
                matches(op?.operation_number) || matches(op?.operation_name)
            ))
        );
    }, [datasource, searchText, selectedPlantFilter, userRole, userPlantCodes.join(',')]);
    // Handlers for Add Shift dialog
    const handleOpenAddDialog = () => {
        setIsAddDialogOpen(true);
        setDialogOpenCount(prevCount => prevCount + 1);
    };
    const handleCloseAddDialog = (event, reason) => {
        if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
            return;
        }
        setIsAddDialogOpen(false);
        setDialogOpenCount(prevCount => Math.max(0, prevCount - 1)); // Ensure count doesn't go below 0
        getComponents(); // Refresh data after add/close
    };
    // Handlers for Edit Shift dialog
    const handleOpenEditDialog = (rowData) => {
        setEditDialogData(rowData); // Set the data for the specific row being edited
        setIsEditDialogOpen(true);
        setDialogOpenCount(prevCount => prevCount + 1);
    };

    const handleCloseEditDialog = (event, reason) => {
        if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
            return;
        }
        setIsEditDialogOpen(false);
        setEditDialogData(null); // Clear the data when dialog closes
        setDialogOpenCount(prevCount => Math.max(0, prevCount - 1)); // Ensure count doesn't go below 0
        getComponents(); // Refresh data after edit/close
    };


    const deleteshift = (row) => {
        console.log("Attempting to delete row:", row);

        Swal.fire({
            title: 'Are you sure you want to delete this record?',
            // text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            allowOutsideClick: false,
            allowEscapeKey: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    // Fetch the current list of shifts from the backend
                    const key = 'component';
                    const currentDataResponse = await customerbasedshift(customerId, key);
                    const allShifts = currentDataResponse[0]?.value || [];

                    // Filter out the shift to be deleted based on its ID
                    const updatedShifts = allShifts.filter(shift => {
                        // Handle both string IDs and MongoDB ObjectId format
                        const shiftId = shift.id?.$oid || shift.id;
                        const rowId = row.id?.$oid || row.id;
                        return shiftId !== rowId;
                    });

                    // Prepare the payload to send back to the API
                    // This structure aligns with how shifts are added/updated
                    const formData = {
                        component: updatedShifts,
                        lastUpdateTs: Date.now() // Update timestamp
                    };

                    const scope = 'SERVER_SCOPE'; // As seen in shiftadd.js context

                    // Call the API to update the shifts (effectively deleting the selected one)
                    const response = await shiftadd(formData, customerId, scope);

                    if (response.msg) {
                        Swal.fire('Deleted!', response.msg, 'success');
                    } else {
                        Swal.fire('Deleted!', 'Your component has been deleted successfully.', 'success');
                    }
                    getComponents(); // Refresh the data in the table after deletion
                } catch (error) {
                    console.error('Error deleting component:', error);
                    Swal.fire('Error!', 'Failed to delete the component: ' + error.message, 'error');
                }
            } else {
                // User clicked "No" or outside the popup
                Swal.fire('Cancelled');
            }
        });
    };

    // CUSTOMER_ID: delete a single nested operation from its parent component.
    const deleteOperation = (component, operation) => {
        Swal.fire({
            title: 'Are you sure you want to delete this operation?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            allowOutsideClick: false,
            allowEscapeKey: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const key = 'component';
                    const currentDataResponse = await customerbasedshift(customerId, key);
                    const allComponents = currentDataResponse[0]?.value || [];

                    const componentId = component.id?.$oid || component.id;
                    const updatedComponents = allComponents.map(comp => {
                        const compId = comp.id?.$oid || comp.id;
                        if (compId === componentId) {
                            return {
                                ...comp,
                                operations: (comp.operations || []).filter(op => op.id !== operation.id)
                            };
                        }
                        return comp;
                    });

                    const formData = {
                        component: updatedComponents,
                        lastUpdateTs: Date.now()
                    };
                    const scope = 'SERVER_SCOPE';
                    const response = await shiftadd(formData, customerId, scope);

                    if (response.msg) {
                        Swal.fire('Deleted!', response.msg, 'success');
                    } else {
                        Swal.fire('Deleted!', 'Operation deleted successfully.', 'success');
                    }
                    getComponents();
                } catch (error) {
                    console.error('Error deleting operation:', error);
                    Swal.fire('Error!', 'Failed to delete the operation: ' + error.message, 'error');
                }
            }
        });
    };

    // const convertTimes = (time) => {
    //     const date = new Date(time);
    //     return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    // };

    const getComponents = async () => {
        try {
            const componentKey = "component";
            const autoTargetKey = "autotarget";

            const [componentRes, autoTargetRes] = await Promise.all([
                customerbasedshift(customerId, componentKey),
                customerbasedshift(customerId, autoTargetKey)
            ]);

            const components = componentRes?.[0]?.value || [];
            const autoTargets = autoTargetRes?.[0]?.value || [];
            console.log(autoTargets, 'autotargets')
            const mappedJson = components.map(comp => {
                const match = autoTargets.find(
                    auto => auto.component_name === comp.component_name
                );

                return {
                    ...comp,
                    auto_target: match
                        ? {
                            cycle_levels: match.cycle_level || "00:00:00",
                            handling_levels: match.handling_level || "00:00:00"
                        }
                        : null
                };
            });

            console.log("Mapped datasource:", mappedJson);
            setDatasource(mappedJson);

        } catch (error) {
            console.error("Error fetching components:", error);
            setDatasource([]);
        }
    };
    console.log('datasource', datasource)

    // const getTotalShiftHours = (start, end, breakTime) => {
    //     const parseTime = (str) => {
    //         const [h, m, s] = str.split(':').map(Number);
    //         return h * 3600 + m * 60 + s;
    //     };

    //     const startSec = parseTime(start);
    //     const endSec = parseTime(end);
    //     const breakSec = parseTime(breakTime);

    //     let totalSec = endSec - startSec - breakSec;

    //     // Handle overnight shifts (e.g., 22:00 to 06:00)
    //     if (totalSec < 0) {
    //         totalSec += 24 * 3600;
    //     }

    //     const hours = String(Math.floor(totalSec / 3600)).padStart(2, '0');
    //     const minutes = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
    //     const seconds = String(totalSec % 60).padStart(2, '0');

    //     return `${hours}:${minutes}:${seconds}`;
    // };


    const paginatedData = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredDatasource.slice(start, start + rowsPerPage);
    }, [filteredDatasource, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        getComponents();
    }, []);

    useEffect(() => {
        setPage(0);
    }, [searchText]);

    const timeToSeconds = (time) => {
        if (!time) return 0;
        const [h = 0, m = 0, s = 0] = String(time).split(":").map(Number);
        return h * 3600 + m * 60 + s;
    };

    const secondsToTime = (sec) => {
        if (sec === '' || sec === null || sec === undefined) return '';
        const sign = sec < 0 ? "-" : "";
        sec = Math.abs(sec);

        const h = String(Math.floor(sec / 3600)).padStart(2, "0");
        const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
        const s = String(sec % 60).padStart(2, "0");

        return `${sign}${h}:${m}:${s}`;
    };

    // Build a quoted CSV from headers + rows and trigger a browser download.
    const downloadCSV = (headers, rows, filename) => {
        const csv = [headers, ...rows]
            .map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Exports the given data (defaults to the current filtered view). Pass the
    // full `datasource` to export every component regardless of the search box.
    const handleExportCSV = (dataOverride) => {
        const data = Array.isArray(dataOverride) ? dataOverride : filteredDatasource;
        // CUSTOMER_ID: operations are nested, so export one row per operation.
        if (isCustomerId) {
            const opHeaders = [
                "Component Name",
                "Component Number",
                "Operation Name",
                "Operation Number",
                "Standard Machine Time",
                "Standard Load/Unload Time",
                "Setup Time"
            ];

            const opRows = data.flatMap(comp => {
                const ops = Array.isArray(comp.operations) ? comp.operations : [];
                if (ops.length === 0) {
                    return [[comp.component_name || "-", comp.component_number || "-", "-", "-", "-", "-", "-"]];
                }
                // Name/Number only on the first operation row; the rest are left
                // blank so extra operations read back as the same component.
                return ops.map((op, i) => [
                    i === 0 ? (comp.component_name || "-") : "",
                    i === 0 ? (comp.component_number || "-") : "",
                    op.operation_name || "-",
                    op.operation_number || "-",
                    op.cycle_time || "-",
                    op.handling_time || "-",
                    op.setupTime || "-"
                ]);
            });

            downloadCSV(opHeaders, opRows, "components.csv");
            return;
        }

        const client = cleanCustomerId(customerId);
        const isGplast = client === window._env_.GPLAST_CUSTOMER_ID;
        const isAtechOrHitech = client === window._env_.ATECH_CUSTOMER_ID || client === window._env_.HITECH_CUSTOMER_ID;
        const isDefaultCustomer = client === window._env_.CUSTOMER_ID;

        // Build headers dynamically based on client config
        const headers = ["Component Name"];
        if (!isGplast) {
            headers.push("Component Number");
        }
        if (isAtechOrHitech) {
            headers.push("Operation Type");
        }
        if (isGplast) {
            headers.push("Item Code", "Process Name", "Operation Number");
        }
        headers.push("Factor", "Factor Value");
        if (isDefaultCustomer) {
            headers.push(
                "Standard Machine Time",
                "Auto Machine Time",
                "Machine Time Diff",
                "Standard Load/Unload Time",
                "Actual Load/Unload Time",
                "Load/Unload Time Diff"
            );
        } else {
            headers.push("Standard Machine Time", "Standard Load/Unload Time");
        }
        headers.push("Setup Time");

        const rows = data.map(row => {
            const rowData = [row.component_name || "-"];
            if (!isGplast) {
                rowData.push(row.component_number || "-");
            }
            if (isAtechOrHitech) {
                rowData.push(row.operation_type || "-");
            }
            if (isGplast) {
                rowData.push(row.item_code || "-", row.process_name || "-", row.operation_number || "-");
            }
            rowData.push(row.factor || "-", row.factorval || "-");

            if (isDefaultCustomer) {
                const cycleTime = row.cycle_time || "-";
                const cycleLevel = row.auto_target?.cycle_levels || "-";
                const handlingTime = row.handling_time || "-";
                const handlingLevel = row.auto_target?.handling_levels || "-";

                const diffCycle =
                    row.cycle_time && row.auto_target?.cycle_levels
                        ? secondsToTime(
                            timeToSeconds(row.cycle_time) -
                            timeToSeconds(row.auto_target?.cycle_levels)
                        )
                        : "-";

                const diffHandling =
                    row.handling_time && row.auto_target?.handling_levels
                        ? secondsToTime(
                            timeToSeconds(row.handling_time) -
                            timeToSeconds(row.auto_target?.handling_levels)
                        )
                        : "-";

                rowData.push(cycleTime, cycleLevel, diffCycle || "-", handlingTime, handlingLevel, diffHandling || "-");
            } else {
                rowData.push(row.cycle_time || "-", row.handling_time || "-");
            }
            rowData.push(row.setupTime || "-");
            return rowData;
        });

        downloadCSV(headers, rows, "components.csv");
    };

    // Downloads a ready-to-fill template: the import headers plus one sample
    // component with multiple operations. Component Name/Number appear only on
    // the first operation row (blank continuation rows read back as the same
    // component), mirroring the real export format.
    const handleSampleExport = () => {
        const headers = [
            "Component Name",
            "Component Number",
            "Operation Name",
            "Operation Number",
            "Standard Machine Time",
            "Standard Load/Unload Time",
            "Setup Time"
        ];
        const rows = [
            ["SampleComponentOne", "CMP-1001", "Turning", "OP-10", "00:01:30", "00:00:20", "00:05:00"],
            ["", "", "Milling", "OP-20", "00:02:15", "00:00:30", "00:04:00"]
        ];
        downloadCSV(headers, rows, "components_sample.csv");
    };


    // ---------- CSV Import (CUSTOMER_ID) ----------
    // Treats the exported "-" placeholder as empty.
    const normalizeCell = (v) => {
        const s = (v ?? '').toString().trim();
        return s === '-' ? '' : s;
    };

    // Accept H:mm:ss or HH:mm:ss; return normalized HH:mm:ss, or null if invalid.
    const parseTimeCell = (raw) => {
        const v = normalizeCell(raw);
        if (!v) return null;
        const m = v.match(/^(\d{1,2}):(\d{1,2}):(\d{1,2})$/);
        if (!m) return null;
        const hh = Number(m[1]), mm = Number(m[2]), ss = Number(m[3]);
        if (hh > 23 || mm > 59 || ss > 59) return null;
        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
    };

    // Sniff the delimiter from the first (non-empty) line, ignoring anything
    // inside quotes. Non-US Excel exports CSVs with ';' (German locale) or tabs;
    // whichever separator appears most in the header wins.
    const detectDelimiter = (text) => {
        let line = '';
        let inQuotes = false;
        for (let i = 0; i < text.length; i++) {
            const ch = text[i];
            if (ch === '"') inQuotes = !inQuotes;
            else if ((ch === '\n' || ch === '\r') && !inQuotes) { if (line) break; }
            else line += ch;
        }
        let best = ',', bestCount = -1;
        for (const d of [',', ';', '\t']) {
            const count = line.split(d).length - 1;
            if (count > bestCount) { bestCount = count; best = d; }
        }
        return best;
    };

    // Minimal CSV parser: strips a leading BOM, auto-detects the delimiter, and
    // handles quoted fields, "" escapes and CRLF endings.
    const parseCSV = (text) => {
        if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1); // strip UTF-8 BOM
        const delimiter = detectDelimiter(text);
        const rows = [];
        let row = [];
        let field = '';
        let inQuotes = false;
        for (let i = 0; i < text.length; i++) {
            const ch = text[i];
            if (inQuotes) {
                if (ch === '"') {
                    if (text[i + 1] === '"') { field += '"'; i++; }
                    else inQuotes = false;
                } else field += ch;
            } else {
                if (ch === '"') inQuotes = true;
                else if (ch === delimiter) { row.push(field); field = ''; }
                else if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
                else if (ch === '\r') { /* ignore */ }
                else field += ch;
            }
        }
        if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
        return rows;
    };

    const genId = () => Math.random().toString(36).substr(2, 9);

    // CUSTOMER_ID import: CSV columns match the Export format. Each row is one
    // operation belonging to a component. Existing operations are updated, new
    // ones are added; components not present in the file are left untouched.
    const processImport = async (text) => {
        const REQUIRED_HEADERS = [
            'Component Name', 'Component Number', 'Operation Name',
            'Operation Number', 'Standard Machine Time', 'Standard Load/Unload Time', 'Setup Time'
        ];

        const rows = parseCSV(text).filter(r => r.some(c => normalizeCell(c) !== ''));
        if (rows.length < 2) {
            Swal.fire('Import cancelled', 'The file has no data rows.', 'error');
            return;
        }

        const headerRow = rows[0].map(h => normalizeCell(h).toLowerCase());
        const missing = REQUIRED_HEADERS.filter(h => !headerRow.includes(h.toLowerCase()));
        if (missing.length > 0) {
            Swal.fire('Import cancelled', 'Missing column(s): ' + missing.join(', '), 'error');
            return;
        }
        const idx = (name) => headerRow.indexOf(name.toLowerCase());

        // ----- Validate every row first (all-or-nothing) -----
        const errors = [];
        const validRows = [];
        const seenKeys = new Map();

        // A row with a blank Component Number is a continuation of the component
        // above it: the export lists a component once, then its extra operations
        // on the following rows. Filling the identity down lets every operation
        // nest under the same component's operations array.
        let lastComponentName = '', lastComponentNumber = '';

        for (let i = 1; i < rows.length; i++) {
            const cells = rows[i];
            const rowNum = i + 1; // 1-based, counting the header row
            const get = (name) => normalizeCell(cells[idx(name)]);

            let component_name = get('Component Name');
            let component_number = get('Component Number');
            if (!component_number) {
                component_number = lastComponentNumber;
                component_name = component_name || lastComponentName;
            } else {
                lastComponentNumber = component_number;
                lastComponentName = component_name || lastComponentName;
            }
            const opName = get('Operation Name');
            const opNum = get('Operation Number');
            const cycleRaw = get('Standard Machine Time');
            const handRaw = get('Standard Load/Unload Time');
            const setupRaw = get('Setup Time');
            const hasOperation = !!(opName || opNum || cycleRaw || handRaw || setupRaw);

            const rowErrors = [];
            if (!component_name) rowErrors.push('Component Name is required');
            else if (component_name.length > 100) rowErrors.push('Component Name exceeds 100 characters');
            if (!component_number) rowErrors.push('Component Number is required');
            else if (component_number.length > 100) rowErrors.push('Component Number exceeds 100 characters');

            let cycle_time = null, handling_time = null, setupTime = null;
            if (hasOperation) {
                if (!opName) rowErrors.push('Operation Name is required');
                else if (/\s/.test(opName)) rowErrors.push('Operation Name must not contain spaces');
                else if (opName.length > 100) rowErrors.push('Operation Name exceeds 100 characters');

                if (!opNum) rowErrors.push('Operation Number is required');
                else if (opNum.length > 100) rowErrors.push('Operation Number exceeds 100 characters');

                cycle_time = parseTimeCell(cycleRaw);
                if (!cycleRaw) rowErrors.push('Standard Machine Time is required');
                else if (!cycle_time) rowErrors.push('Standard Machine Time is invalid (use HH:mm:ss)');
                else if (cycle_time === '00:00:00') rowErrors.push('Standard Machine Time must not be 00:00:00');

                handling_time = parseTimeCell(handRaw);
                if (!handRaw) rowErrors.push('Standard Load/Unload Time is required');
                else if (!handling_time) rowErrors.push('Standard Load/Unload Time is invalid (use HH:mm:ss)');

                setupTime = parseTimeCell(setupRaw);
                if (!setupRaw) rowErrors.push('Setup Time is required');
                else if (!setupTime) rowErrors.push('Setup Time is invalid (use HH:mm:ss)');
            }

            // Duplicate operation within the file itself.
            if (hasOperation && rowErrors.length === 0) {
                const key = `${component_number.toLowerCase()}||${opNum.toLowerCase()}`;
                if (seenKeys.has(key)) {
                    rowErrors.push(`Duplicate of row ${seenKeys.get(key)} (same Component + Operation Number)`);
                } else {
                    seenKeys.set(key, rowNum);
                }
            }

            if (rowErrors.length > 0) {
                errors.push({
                    rowNum,
                    messages: rowErrors,
                    // Raw cell values (as typed) so the dialog can show the offending row.
                    data: {
                        component_name, component_number,
                        operation_name: opName, operation_number: opNum,
                        cycle_time: cycleRaw, handling_time: handRaw, setupTime: setupRaw
                    }
                });
            } else {
                validRows.push({
                    component_name, component_number, hasOperation,
                    operation_name: opName, operation_number: opNum,
                    cycle_time, handling_time, setupTime
                });
            }
        }

        // If ANY row is invalid, import nothing. Surface the offending rows in
        // the dialog's error table (highlighted red) so the user can fix and
        // re-upload; Upload stays disabled while errors exist.
        if (errors.length > 0) {
            setImportErrors(errors);
            setImportPreview(null);
            return;
        }
        if (validRows.length === 0) {
            Swal.fire('Import cancelled', 'No valid rows to import.', 'error');
            return;
        }

        // ----- Guard 1: only proceed on a GOOD fetch -----
        // A failed or malformed read must never be treated as "empty", or we
        // would overwrite every existing component with just the imported rows.
        let fetched;
        try {
            const res = await customerbasedshift(customerId, 'component');
            const value = res?.[0]?.value;
            if (!Array.isArray(value)) {
                Swal.fire('Import cancelled', 'Could not read current components from the server. Please try again.', 'error');
                return;
            }
            fetched = value;
        } catch (err) {
            Swal.fire('Import cancelled', 'Failed to load current components: ' + (err?.message || err), 'error');
            return;
        }

        // ----- Guard 2: existing data present but server returned empty -----
        // The screen already shows components but the fetch came back empty:
        // treat as a bad read and abort rather than wipe the existing data.
        if (datasource.length > 0 && fetched.length === 0) {
            Swal.fire('Import cancelled', 'Existing components look empty unexpectedly. Aborting to protect your data.', 'error');
            return;
        }

        // Work on a clone so a mid-way error never mutates the fetched snapshot.
        const allComponents = fetched.map(c => ({
            ...c,
            operations: Array.isArray(c.operations) ? c.operations.map(op => ({ ...op })) : []
        }));

        const norm = (val) => (val || '').trim().toLowerCase();
        let newComponents = 0, opsAdded = 0, opsUpdated = 0;

        for (const r of validRows) {
            let compIdx = allComponents.findIndex(c => norm(c.component_number) === norm(r.component_number));
            if (compIdx === -1) {
                allComponents.push({
                    id: genId(),
                    component_name: r.component_name,
                    component_number: r.component_number,
                    factor: '',
                    factorval: '',
                    operations: [],
                    __status: 'new' // UI-only tag for the preview; stripped before save
                });
                compIdx = allComponents.length - 1;
                newComponents++;
            } else {
                allComponents[compIdx].component_name = r.component_name;
            }

            if (r.hasOperation) {
                const ops = allComponents[compIdx].operations;
                const opIdx = ops.findIndex(op => norm(op.operation_number) === norm(r.operation_number));
                const opData = {
                    operation_name: r.operation_name,
                    operation_number: r.operation_number,
                    cycle_time: r.cycle_time,
                    handling_time: r.handling_time,
                    setupTime: r.setupTime
                };
                if (opIdx === -1) {
                    ops.push({ id: genId(), ...opData, __status: 'new' });
                    opsAdded++;
                } else {
                    // Only an actual value change counts as an update; re-importing
                    // an unchanged row must not inflate the "updated" count.
                    const cur = ops[opIdx];
                    const eq = (a, b) => String(a ?? '').trim() === String(b ?? '').trim();
                    const changed =
                        !eq(cur.operation_name, opData.operation_name) ||
                        !eq(cur.cycle_time, opData.cycle_time) ||
                        !eq(cur.handling_time, opData.handling_time) ||
                        !eq(cur.setupTime, opData.setupTime);
                    ops[opIdx] = { ...cur, ...opData, ...(changed ? { __status: 'updated' } : {}) };
                    if (changed) opsUpdated++;
                }
            }
        }

        // Show the new/changed rows in the upload dialog's preview and let the
        // user confirm from there. The actual save happens in confirmImport();
        // allComponents/fetched are carried through untouched. An unchanged
        // re-import yields an empty `components` list, which the dialog renders
        // as "No changes".
        const previewComponents = allComponents.filter(
            c => c.__status === 'new' || (c.operations || []).some(op => op.__status)
        );
        setImportPreview({
            components: previewComponents,
            allComponents,
            fetched,
            stats: { newComponents, opsAdded, opsUpdated }
        });
    };

    // Strip UI-only __status tags and persist the merged component set. Runs on
    // the dialog's Upload button once the user has reviewed the preview.
    const confirmImport = async () => {
        if (!importPreview || importPreview.components.length === 0) return;
        setIsCommitting(true);
        const { allComponents, fetched, stats } = importPreview;
        const clean = allComponents.map(({ __status, ...comp }) => ({
            ...comp,
            operations: (comp.operations || []).map(({ __status: _s, ...op }) => op)
        }));
        try {
            // ----- Guard 3: keep a recoverable snapshot before overwriting -----
            // Best-effort: written to a separate attribute key, never blocks import.
            try {
                await shiftadd({ component_backup: fetched, lastUpdateTs: Date.now() }, customerId, 'SERVER_SCOPE');
            } catch (_) { /* backup is best-effort */ }

            const response = await shiftadd(
                { component: clean, lastUpdateTs: Date.now() },
                customerId,
                'SERVER_SCOPE'
            );
            setImportPreview(null);
            setImportFileName('');
            setIsImportDialogOpen(false);
            await getComponents();
            Swal.fire(
                'Import successful',
                response?.msg || `${stats.opsAdded + stats.opsUpdated} operation(s) imported (${stats.newComponents} new component(s)).`,
                'success'
            );
        } catch (err) {
            Swal.fire('Import failed', 'Error saving imported data: ' + (err?.message || err), 'error');
        } finally {
            setIsCommitting(false);
        }
    };

    // Import CSV button just opens the upload dialog; the template download,
    // file drop and preview all live inside it.
    const handleImportClick = () => {
        setImportPreview(null);
        setImportErrors([]);
        setImportFileName('');
        setIsDragging(false);
        setIsImportDialogOpen(true);
    };

    const handleImportDialogClose = () => {
        if (isCommitting) return; // don't close mid-save
        setIsImportDialogOpen(false);
        setImportPreview(null);
        setImportErrors([]);
        setImportFileName('');
        setIsDragging(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleResetImport = () => {
        setImportPreview(null);
        setImportErrors([]);
        setImportFileName('');
        setIsDragging(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const MAX_IMPORT_MB = 10;

    // Validate, read and parse a dropped/browsed file. Only CSV is supported
    // (there is no Excel parser bundled), so .xls/.xlsx are rejected with a hint.
    const processFile = async (file) => {
        if (!file) return;
        const name = file.name || '';
        if (!/\.csv$/i.test(name)) {
            Swal.fire('Unsupported file', 'Please upload a .csv file. For Excel, use “Save As → CSV” first.', 'error');
            return;
        }
        if (file.size > MAX_IMPORT_MB * 1024 * 1024) {
            Swal.fire('File too large', `Maximum file size is ${MAX_IMPORT_MB} MB.`, 'error');
            return;
        }
        setImportFileName(name);
        setImportPreview(null);
        setImportErrors([]);
        try {
            const text = await file.text();
            await processImport(text);
        } catch (err) {
            Swal.fire('Import failed', 'Could not read the file: ' + (err?.message || err), 'error');
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        await processFile(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDragOver = (e) => { e.preventDefault(); if (!isDragging) setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer?.files?.[0];
        processFile(file);
    };

    // Preview table cells hug their content (nowrap + auto table layout) so
    // column widths follow the data rather than stretching to fill the dialog.
    const PREVIEW_HEAD = { whiteSpace: 'nowrap', fontWeight: 'bold', background: '#f5f5f5', color: '#333', px: 1.5, py: 1 };
    const PREVIEW_CELL = { whiteSpace: 'nowrap', px: 1.5, py: 0.75 };


    return (
        <div className="pages" style={{
            paddingLeft: "10px"
        }}>
            <div className="pagecontents">
                <div className="left-labels">
                    <div className="shift-content">
                        <h5>Component Registration</h5>
                        <div className="add_new">
                            <Tooltip title="Add Component">
                                <IconButton className="circle" onClick={handleOpenAddDialog}>
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                            <ComponentAdd
                                open={isAddDialogOpen} // Use specific state for add dialog
                                handleClose={handleCloseAddDialog} // Use specific close handler
                                handleAdd={handleCloseAddDialog} // Call close handler on successful add
                                dialogOpenCount={dialogOpenCount}
                                datasource={datasource}
                                customerId={customerId}
                                setDatasource={setDatasource}
                            // dialogData is intentionally omitted for ShiftAdd to ensure it's empty
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Button
                            variant="outlined"
                            onClick={() => handleExportCSV(datasource)}
                            sx={{
                                minWidth: 120,
                                height: 54,
                                borderColor: '#f47803',
                                color: '#f47803',
                                '&:hover': { borderColor: '#f47803', color: '#f47803', background: '#e9e9e9' },
                                textTransform: 'none',
                                fontSize: "16px",
                                fontWeight: 'bold',
                            }}
                        >
                            Export CSV
                        </Button>
                        {cleanCustomerId(customerId) === window._env_.BIOTEK_CUSTOMER_ID && (
                            <>
                                <input
                                    type="file"
                                    accept=".csv,text/csv"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={handleImportClick}
                                    sx={{
                                        minWidth: 120,
                                        height: 54,
                                        borderColor: '#f47803',
                                        color: '#f47803',
                                        '&:hover': { borderColor: '#f47803', color: '#f47803', background: '#e9e9e9' },
                                        textTransform: 'none',
                                        fontSize: "16px",
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Import CSV
                                </Button>
                            </>
                        )}
                        <TextField
                            label={isCustomerId
                                ? "Search Component Name, Number or Operation Name/Number"
                                : "Search Component Name or Number"}
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputLabelProps={{
                                sx: { color: 'black', '&.Mui-focused': { color: 'orange' } },
                            }}
                            sx={{
                                minWidth: 300
                            }}
                        />
                        {showPlantFilter && (
                            <Autocomplete
                                options={[
                                    { label: 'All Plants', value: 'all' },
                                    ...plantOptions.map((p) => ({ label: p.name, value: p.code }))
                                ]}
                                getOptionLabel={(o) => o.label}
                                value={
                                    selectedPlantFilter === 'all'
                                        ? { label: 'All Plants', value: 'all' }
                                        : { label: resolvePlantName(plantOptions, selectedPlantFilter), value: selectedPlantFilter }
                                }
                                onChange={(_, newValue) => setSelectedPlantFilter(newValue?.value || 'all')}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Filter by Plant"
                                        variant="outlined"
                                        sx={{ minWidth: 220 }}
                                    />
                                )}
                            />
                        )}
                    </div>
                </div>

                <Card className="card_sec">
                    <div className="example-container">
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                {isCustomerId ? (
                                    <TableRow>
                                        <TableCell>Component Name</TableCell>
                                        <TableCell>Component Number</TableCell>
                                        <TableCell>Operation Name</TableCell>
                                        <TableCell>Operation Number</TableCell>
                                        <TableCell sx={{ minWidth: 130, whiteSpace: 'normal', fontSize: '0.75rem' }}>Standard<br />Machine Time</TableCell>
                                        <TableCell sx={{ minWidth: 150, whiteSpace: 'normal', fontSize: '0.75rem' }}>Standard<br />Load/Unload Time</TableCell>
                                        <TableCell>Setup Time</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                ) : (
                                    <TableRow>
                                        <TableCell>Component Name</TableCell>
                                        {(cleanCustomerId(customerId) != window._env_.GPLAST_CUSTOMER_ID) && (<TableCell>Component Number</TableCell>)}
                                        {showPlantFilter && (<TableCell>Plant</TableCell>)}
                                        {(cleanCustomerId(customerId) === window._env_.ATECH_CUSTOMER_ID || cleanCustomerId(customerId) === window._env_.HITECH_CUSTOMER_ID) && (<TableCell>Operation Type</TableCell>)}
                                        {(cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID) && (<TableCell>Item Code</TableCell>)}
                                        {(cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID) && (<TableCell>Process Name</TableCell>)}
                                        {(cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID) && (<TableCell>Operation Number</TableCell>)}
                                        {/* <TableCell>Route Card</TableCell> */}
                                        {/* <TableCell>Drawing Code</TableCell> */}
                                        {/* <TableCell>Mould Name</TableCell> */}
                                        {/* <TableCell>Mould Number</TableCell> */}
                                        <TableCell>Factor</TableCell>
                                        <TableCell>Factor Value</TableCell>
                                        <TableCell sx={{ minWidth: 130, whiteSpace: 'normal', fontSize: '0.75rem' }}>Standard<br />Machine Time</TableCell>
                                        {cleanCustomerId(customerId) === window._env_.CUSTOMER_ID && (<TableCell>Actual Machine Time</TableCell>)}
                                        <TableCell sx={{ minWidth: 150, whiteSpace: 'normal', fontSize: '0.75rem' }}>Standard<br />Load/Unload Time</TableCell>
                                        {cleanCustomerId(customerId) === window._env_.CUSTOMER_ID && (<TableCell>Actual load/unload Time</TableCell>)}
                                        <TableCell>Setup Time</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                )}
                            </TableHead>
                            <TableBody>
                                {isCustomerId ? (
                                    paginatedData.length > 0 ? (
                                        paginatedData.map((component, cIdx) => {
                                            const ops = Array.isArray(component.operations) ? component.operations : [];
                                            const compKey = getComponentKey(component, cIdx);
                                            const isExpanded = !!expandedComponents[compKey];
                                            const rowClass = classNames({ 'odd-row': cIdx % 2 !== 0, 'even-row': cIdx % 2 === 0 });
                                            const firstOp = ops[0];
                                            const restOps = ops.slice(1);
                                            const hasMore = restOps.length > 0;
                                            return (
                                                <Fragment key={compKey}>
                                                    {/* Component row shows the first operation inline */}
                                                    <TableRow>
                                                        <TableCell className={rowClass}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <Tooltip title={hasMore ? (isExpanded ? 'Hide operations' : 'Show operations') : ''}>
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => toggleComponent(compKey)}
                                                                            disabled={!hasMore}
                                                                            sx={{ visibility: hasMore ? 'visible' : 'hidden' }}
                                                                        >
                                                                            {isExpanded ? <KeyboardArrowDownIcon sx={{ color: 'black' }} /> : <KeyboardArrowRightIcon sx={{ color: 'black' }} />}
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                                <span>{component.component_name || '---'}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className={rowClass}>{component.component_number || '---'}</TableCell>
                                                        <TableCell className={rowClass}>{firstOp?.operation_name || '---'}</TableCell>
                                                        <TableCell className={rowClass}>{firstOp?.operation_number || '---'}</TableCell>
                                                        <TableCell className={rowClass}>{firstOp?.cycle_time || '---'}</TableCell>
                                                        <TableCell className={rowClass}>{firstOp?.handling_time || '---'}</TableCell>
                                                        <TableCell className={rowClass}>{firstOp?.setupTime || '---'}</TableCell>
                                                        <TableCell className={rowClass}>
                                                            {/* Always show Edit: with an operation it edits that op inline,
                                                                without one it opens the component to add its first operation. */}
                                                            <Tooltip title={firstOp ? "Edit Operation" : "Edit Component"}>
                                                                <IconButton onClick={() => handleOpenEditDialog(firstOp ? { ...component, __editingOperation: firstOp } : component)}>
                                                                    <EditIcon sx={{ color: 'black' }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete Component">
                                                                <IconButton onClick={() => deleteshift(component, datasource)}>
                                                                    <DeleteIcon sx={{ color: 'black' }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                    {/* Remaining operations appear as nested rows when expanded */}
                                                    {isExpanded && restOps.map((op, oIdx) => (
                                                        <TableRow key={`${compKey}-${op.id || oIdx}`}>
                                                            <TableCell className={rowClass} />
                                                            <TableCell className={rowClass} />
                                                            <TableCell className={rowClass} sx={{ pl: 4 }}>{op.operation_name || '---'}</TableCell>
                                                            <TableCell className={rowClass}>{op.operation_number || '---'}</TableCell>
                                                            <TableCell className={rowClass}>{op.cycle_time || '---'}</TableCell>
                                                            <TableCell className={rowClass}>{op.handling_time || '---'}</TableCell>
                                                            <TableCell className={rowClass}>{op.setupTime || '---'}</TableCell>
                                                            <TableCell className={rowClass}>
                                                                <Tooltip title="Edit Operation">
                                                                    <IconButton onClick={() => handleOpenEditDialog({ ...component, __editingOperation: op })}>
                                                                        <EditIcon sx={{ color: 'black' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Delete Operation">
                                                                    <IconButton onClick={() => deleteOperation(component, op)}>
                                                                        <DeleteIcon sx={{ color: 'black' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </Fragment>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" style={{
                                                padding: '20px', background: '#EDEDED', fontSize: '1rem',
                                                letterSpacing: ' 0.02rem'
                                            }}>
                                                No Results found
                                            </TableCell>
                                        </TableRow>
                                    )
                                ) : paginatedData.length > 0 ? (
                                    paginatedData.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                {row.component_name || '---'}
                                            </TableCell>
                                            {(cleanCustomerId(customerId) != window._env_.GPLAST_CUSTOMER_ID) && (<TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                {row.component_number || '---'}
                                            </TableCell>)}
                                            {showPlantFilter && (<TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                {row.plant_name || resolvePlantName(plantOptions, row.plant_code) || '---'}
                                            </TableCell>)}
                                            {(cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID) && (<TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                {row?.item_code || '---'}
                                            </TableCell>)}
                                            {(cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID) && (<TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                {row?.process_name || '---'}
                                            </TableCell>)}
                                            {(cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID) && (<TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                {row?.operation_number || '---'}
                                            </TableCell>)}
                                            {(cleanCustomerId(customerId) === window._env_.ATECH_CUSTOMER_ID || cleanCustomerId(customerId) === window._env_.HITECH_CUSTOMER_ID) && (<TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                {row.operation_type || '---'}
                                            </TableCell>)}
                                            <TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                {row.factor || '---'}
                                            </TableCell>
                                            <TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                {row.factorval || '---'}
                                            </TableCell>
                                            <TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                {row.cycle_time || '---'}
                                            </TableCell>
                                            {cleanCustomerId(customerId) === window._env_.CUSTOMER_ID && (
                                                <TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                    {row.auto_target?.cycle_levels || '---'}
                                                </TableCell>)}
                                            <TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                {row.handling_time || '---'}
                                            </TableCell>
                                            {cleanCustomerId(customerId) === window._env_.CUSTOMER_ID && (
                                                <TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                    {row.auto_target?.handling_levels || '---'}
                                                </TableCell>)}
                                            <TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                {row.setupTime || '---'}
                                            </TableCell>
                                            <TableCell className={classNames({ 'odd-row': index % 2 !== 0, 'even-row': index % 2 === 0 })}>
                                                <Tooltip title="Edit Component">
                                                    <IconButton onClick={() => handleOpenEditDialog(row)}>
                                                        <EditIcon sx={{ color: 'black' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Component">
                                                    <IconButton onClick={() => deleteshift(row, datasource)}>
                                                        <DeleteIcon sx={{ color: 'black' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} align="center" style={{
                                            padding: '20px', background: '#EDEDED', fontSize: '1rem',
                                            letterSpacing: ' 0.02rem'
                                        }}>
                                            No Results found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>

                        </Table>
                    </div>
                    <CardActions sx={{ px: 2, justifyContent: 'end', background: '#dddddd' }}>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={filteredDatasource.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Items per page"
                        />
                    </CardActions>
                </Card>
            </div>
            {/* Render ShiftEdit conditionally outside the table loop */}
            {isEditDialogOpen && (
                <ComponentEdit
                    open={isEditDialogOpen} // Use specific state for edit dialog
                    handleClose={handleCloseEditDialog} // Use specific close handler
                    handleAdd={handleCloseEditDialog} // Call close handler on successful edit
                    dialogData={editDialogData} // Pass the stored row data
                    dialogOpenCount={dialogOpenCount}
                    datasource={datasource}
                    customerId={customerId}
                    setDatasource={setDatasource}
                />
            )}

            {/* Bulk CSV Upload dialog: download template, drop a file, preview
                the new/updated rows, then Upload to commit. */}
            <Dialog
                open={isImportDialogOpen}
                onClose={handleImportDialogClose}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2, backgroundColor: '#fff' } }}
            >
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 20px', borderBottom: '1px solid #e0e0e0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CloudUploadIcon sx={{ color: '#f47803' }} />
                        <span style={{ fontSize: 18, fontWeight: 'bold', color: '#222' }}>
                            Bulk CSV Upload — Component Registration
                        </span>
                    </div>
                    <IconButton onClick={handleImportDialogClose} disabled={isCommitting} size="small">
                        <CloseIcon />
                    </IconButton>
                </div>

                <DialogContent sx={{ backgroundColor: '#fff' }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 16, flexWrap: 'wrap', margin: '8px 0 16px'
                    }}>
                        <span style={{ color: '#555' }}>
                            Download the template, fill in data, then upload. <b>Component Number</b> is used to auto-detect create vs update.
                        </span>
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleSampleExport}
                            sx={{
                                borderColor: '#f47803', color: '#f47803', textTransform: 'none',
                                fontWeight: 'bold', whiteSpace: 'nowrap',
                                '&:hover': { borderColor: '#f47803', background: '#fff4e5' }
                            }}
                        >
                            Download Template
                        </Button>
                    </div>

                    <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                            border: `2px dashed ${isDragging ? '#f47803' : '#c4c4c4'}`,
                            borderRadius: 8, padding: '32px 16px', textAlign: 'center',
                            cursor: 'pointer', background: isDragging ? '#fff4e5' : '#fafafa',
                            transition: 'all .15s'
                        }}
                    >
                        <CloudUploadIcon sx={{ fontSize: 48, color: '#b0b0b0' }} />
                        <div style={{ fontWeight: 'bold', color: '#333', marginTop: 8 }}>
                            {importFileName || 'Drag & drop a file here'}
                        </div>
                        <div style={{ color: '#777', marginTop: 4, fontSize: 13 }}>
                            .csv &nbsp;|&nbsp; <span style={{ textDecoration: 'underline', color: '#f47803' }}>click to browse</span> (max {MAX_IMPORT_MB} MB)
                        </div>
                    </div>

                    {importErrors.length > 0 && (
                        <div style={{ marginTop: 20 }}>
                            <div style={{ color: '#c62828', fontWeight: 'bold', marginBottom: 8 }}>
                                {importErrors.length} row(s) have errors — fix them and re-upload. Nothing was imported.
                            </div>
                            <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid #f5c6cb', borderRadius: 6 }}>
                                <Table size="small" sx={{ width: 'auto', tableLayout: 'auto' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={PREVIEW_HEAD}>Row</TableCell>
                                            <TableCell sx={PREVIEW_HEAD}>Component Name</TableCell>
                                            <TableCell sx={PREVIEW_HEAD}>Component Number</TableCell>
                                            <TableCell sx={PREVIEW_HEAD}>Operation Name</TableCell>
                                            <TableCell sx={PREVIEW_HEAD}>Operation Number</TableCell>
                                            <TableCell sx={PREVIEW_HEAD}>Standard Machine Time</TableCell>
                                            <TableCell sx={PREVIEW_HEAD}>Standard Load/Unload Time</TableCell>
                                            <TableCell sx={PREVIEW_HEAD}>Setup Time</TableCell>
                                            <TableCell sx={PREVIEW_HEAD}>Errors</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {importErrors.map((err) => (
                                            <TableRow key={`err-${err.rowNum}`} sx={{ backgroundColor: '#fdecea' }}>
                                                <TableCell sx={{ ...PREVIEW_CELL, color: '#c62828', fontWeight: 'bold' }}>{err.rowNum}</TableCell>
                                                <TableCell sx={PREVIEW_CELL}>{err.data.component_name || '---'}</TableCell>
                                                <TableCell sx={PREVIEW_CELL}>{err.data.component_number || '---'}</TableCell>
                                                <TableCell sx={PREVIEW_CELL}>{err.data.operation_name || '---'}</TableCell>
                                                <TableCell sx={PREVIEW_CELL}>{err.data.operation_number || '---'}</TableCell>
                                                <TableCell sx={PREVIEW_CELL}>{err.data.cycle_time || '---'}</TableCell>
                                                <TableCell sx={PREVIEW_CELL}>{err.data.handling_time || '---'}</TableCell>
                                                <TableCell sx={PREVIEW_CELL}>{err.data.setupTime || '---'}</TableCell>
                                                <TableCell sx={{ ...PREVIEW_CELL, color: '#c62828', whiteSpace: 'normal', minWidth: 220 }}>{err.messages.join('; ')}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {importPreview && (
                        <div style={{ marginTop: 20 }}>
                            {importPreview.components.length === 0 ? (
                                <div style={{
                                    textAlign: 'center', padding: '20px', color: '#666',
                                    background: '#f5f5f5', borderRadius: 6
                                }}>
                                    No changes
                                </div>
                            ) : (
                                <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 6 }}>
                                    <Table size="small" sx={{ width: 'auto', tableLayout: 'auto' }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={PREVIEW_HEAD}>Component Name</TableCell>
                                                <TableCell sx={PREVIEW_HEAD}>Component Number</TableCell>
                                                <TableCell sx={PREVIEW_HEAD}>Operation Name</TableCell>
                                                <TableCell sx={PREVIEW_HEAD}>Operation Number</TableCell>
                                                <TableCell sx={PREVIEW_HEAD}>Standard Machine Time</TableCell>
                                                <TableCell sx={PREVIEW_HEAD}>Standard Load/Unload Time</TableCell>
                                                <TableCell sx={PREVIEW_HEAD}>Setup Time</TableCell>
                                                <TableCell sx={PREVIEW_HEAD}>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {importPreview.components.map((component, cIdx) => {
                                                const compKey = getComponentKey(component, cIdx);
                                                const ops = Array.isArray(component.operations) ? component.operations : [];
                                                const rows = ops.length ? ops : [null];
                                                const compNew = component.__status === 'new';
                                                return (
                                                    <Fragment key={`dlg-preview-${compKey}`}>
                                                        {rows.map((op, i) => {
                                                            const isNew = op?.__status === 'new' || (compNew && !op?.__status);
                                                            const isUpdated = op?.__status === 'updated';
                                                            const bg = isUpdated ? '#fff4e5' : isNew ? '#e6f4ea' : 'transparent';
                                                            const label = isUpdated ? 'Updated' : compNew ? 'New' : isNew ? 'New Op' : '—';
                                                            const color = isUpdated ? '#b26a00' : isNew ? '#1b7a3d' : '#888';
                                                            return (
                                                                <TableRow key={`${compKey}-dlg-${i}`} sx={{ backgroundColor: bg }}>
                                                                    <TableCell sx={PREVIEW_CELL}>{i === 0 ? (component.component_name || '---') : ''}</TableCell>
                                                                    <TableCell sx={PREVIEW_CELL}>{i === 0 ? (component.component_number || '---') : ''}</TableCell>
                                                                    <TableCell sx={PREVIEW_CELL}>{op?.operation_name || '---'}</TableCell>
                                                                    <TableCell sx={PREVIEW_CELL}>{op?.operation_number || '---'}</TableCell>
                                                                    <TableCell sx={PREVIEW_CELL}>{op?.cycle_time || '---'}</TableCell>
                                                                    <TableCell sx={PREVIEW_CELL}>{op?.handling_time || '---'}</TableCell>
                                                                    <TableCell sx={PREVIEW_CELL}>{op?.setupTime || '---'}</TableCell>
                                                                    <TableCell sx={{ ...PREVIEW_CELL, color, fontWeight: 'bold' }}>{label}</TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </Fragment>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleResetImport}
                        disabled={isCommitting}
                        sx={{ textTransform: 'none', borderColor: '#bbb', color: '#333' }}
                    >
                        Reset
                    </Button>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            variant="contained"
                            startIcon={isCommitting ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                            onClick={confirmImport}
                            disabled={isCommitting || importErrors.length > 0 || !importPreview || importPreview.components.length === 0}
                            sx={{ textTransform: 'none', fontWeight: 'bold', background: '#f47803', '&:hover': { background: '#d96a00' } }}
                        >
                            {isCommitting ? 'Uploading…' : 'Upload'}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleImportDialogClose}
                            disabled={isCommitting}
                            sx={{ textTransform: 'none', borderColor: '#bbb', color: '#333' }}
                        >
                            Close
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ComponentRegistration;